import * as React from 'react'

import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'

import type { EngagementStatus } from '../../graphqlEnums'
import { MentionData_Mention } from '../../Mentions/fragments/mentionData'
import type { MentionData } from '../../Mentions/fragments/mentionData'
import { getPublishedAtRange } from '../../CommunityFilters/getPublishedAtRange'
import { DEFAULT_SORT_OPTION } from '../../CommunityFilters/types'
import type {
  BaseCommunityFilters,
  SortOption,
} from '../../CommunityFilters/types'

const appendQuery = (
  prev: MentionInboxQuery,
  { fetchMoreResult }: { fetchMoreResult: MentionInboxQuery | null },
): MentionInboxQuery => {
  if (!fetchMoreResult) return prev
  return {
    engagementInboxByChannelIds: {
      ...fetchMoreResult.engagementInboxByChannelIds,
      edges: [
        ...prev.engagementInboxByChannelIds.edges,
        ...fetchMoreResult.engagementInboxByChannelIds.edges,
      ],
    },
  }
}

export const MENTIONS_PER_PAGE = 20

// Derived from the gql.tada document (replaces the dropped codegen types).
type MentionInboxQuery = ResultOf<typeof MentionInboxQueryGQL>
type MentionInboxQueryVariables = VariablesOf<typeof MentionInboxQueryGQL>

/**
 * Maps the shared Community sort selector onto the engagement `sort` input.
 *
 * The mentions BE sorts by published date only (`MentionsSortField` in
 * `@buffer-mono/entities` — a `status` sort field would be silently dropped
 * by `parseSortInput`), so `unansweredFirst` degrades to `newest` explicitly.
 * The sort menu hides "Unanswered first" for mentions for the same reason,
 * but the `sort` URL param is shared across tabs, so the value can still
 * arrive here after being set on the Comments tab.
 */
function sortOptionToSort(
  sortOption: SortOption,
): NonNullable<MentionInboxQueryVariables['input']['sort']> {
  switch (sortOption) {
    case 'oldest':
      return [{ field: 'publishedAt', direction: 'asc' }]
    case 'unansweredFirst':
    case 'newest':
    default:
      return [{ field: 'publishedAt', direction: 'desc' }]
  }
}

export const MentionInboxQueryGQL = graphql(
  /* GraphQL */ `
    query MentionInbox(
      $input: EngagementInboxByChannelIdsInput!
      $first: Int!
      $after: String
    ) {
      engagementInboxByChannelIds(input: $input, first: $first, after: $after) {
        edges {
          cursor
          node {
            __typename
            ... on Mention {
              ...MentionData_Mention
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `,
  [MentionData_Mention],
)

type UseMentionsQueryProps = {
  // Only `channel.id` is read (to scope + de-dupe the inbox); typed
  // structurally so the hook accepts the sidebar producer's channel node.
  channels: ReadonlyArray<{ id: string }>
  /**
   * Mention filters mirror `BaseCommunityFilters` for symmetry with the
   * Comments tab. Status values `'all'` and `'new'` are FE-only and map
   * to "no status filter" on the wire. The mentions filter UI (Phase 6
   * of the plan) will eventually pivot to `EngagementStatus`-derived
   * options and this type narrows then.
   */
  filters?: BaseCommunityFilters
  skip?: boolean
}

type UseMentionsQueryReturn = {
  data: MentionInboxQuery | undefined
  mentions: MentionData[]
  loading: boolean
  error: ErrorLike | undefined
  fetchingMore: boolean
  lastElementRef: (node: Element | null) => void
}

/**
 * Mentions inbox query. Narrows the polymorphic `Engagement` node onto
 * `Mention` via `__typename`.
 */
export function useMentionsQuery({
  channels,
  filters,
  skip,
}: UseMentionsQueryProps): UseMentionsQueryReturn {
  const organizationId = useOrganizationId()

  const channelIds = React.useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )

  const { status, publishedAt, sortOption } = filters ?? {}
  // FE `FilterStatus` includes `'all'` and `'new'` which have no BE
  // counterpart on `EngagementStatus` — strip them at the boundary.
  const mentionStatus: EngagementStatus | undefined =
    status === 'unreplied' || status === 'replied' || status === 'dismissed'
      ? status
      : undefined

  // Resolved once per publishedAt change; hour-stable so all consumers share
  // the same Apollo cache entry (see getPublishedAtRange).
  const { dateStart } = React.useMemo(
    () => getPublishedAtRange(publishedAt),
    [publishedAt],
  )

  // Stable variables — channelIds sorted+joined to avoid refetches when
  // the array identity changes but the contents don't.
  const channelIdsKey = channelIds.toSorted().join('\0')
  const stableChannelIds = React.useMemo(
    () => (channelIdsKey ? channelIdsKey.split('\0') : []),
    [channelIdsKey],
  )
  const variables: MentionInboxQueryVariables = React.useMemo(
    () => ({
      input: {
        organizationId: organizationId ?? '',
        engagementType: 'mention',
        channelIds: stableChannelIds,
        filters: {
          status: mentionStatus,
          publishedAt: {
            from: dateStart ?? undefined,
          },
        },
        sort: sortOptionToSort(sortOption ?? DEFAULT_SORT_OPTION),
      },
      first: MENTIONS_PER_PAGE,
      after: null,
    }),
    [organizationId, stableChannelIds, mentionStatus, dateStart, sortOption],
  )

  const { data, loading, error, fetchMore, networkStatus } = useQuery(
    MentionInboxQueryGQL,
    {
      variables,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !organizationId || skip || channelIds.length === 0,
    },
  )

  const fetchingMore = networkStatus === NetworkStatus.fetchMore

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage: data?.engagementInboxByChannelIds.pageInfo.hasNextPage,
    fetchMore: async () => {
      try {
        await fetchMore({
          variables: {
            ...variables,
            after: data?.engagementInboxByChannelIds.pageInfo.endCursor ?? null,
          },
          updateQuery: appendQuery,
        })
      } catch (fetchMoreMentionsError) {
        toast.error('Error fetching more mentions')
        throw new Error(
          'Failed to fetch more mentions: ' +
            (fetchMoreMentionsError instanceof Error
              ? fetchMoreMentionsError
              : new Error(String(fetchMoreMentionsError))
            ).message,
          { cause: fetchMoreMentionsError },
        )
      }
    },
  })

  const mentions = React.useMemo<MentionData[]>(() => {
    // Narrowing inside the callback is what types these as `MentionData`: the
    // `Mention` branch spreads `MentionData_Mention`, and a `filter` predicate
    // would not carry that branch through to the mapped result.
    const rawMentions =
      data?.engagementInboxByChannelIds.edges.flatMap((edge) =>
        edge.node.__typename === 'Mention' ? [edge.node] : [],
      ) ?? []

    // Scope mentions to the requested channels. The BE already restricts
    // results to the user's accessible channels via `resolveChannelIdsAccess`,
    // so this guards against any channelId outside the current selection.
    const channelIdSet = new Set(channels.map((channel) => channel.id))
    return rawMentions.filter((mention) => channelIdSet.has(mention.channelId))
  }, [data, channels])

  return React.useMemo(
    () => ({
      data,
      mentions,
      loading,
      error,
      fetchingMore,
      lastElementRef,
    }),
    [data, mentions, loading, error, fetchingMore, lastElementRef],
  )
}
